import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isPropertiesDropdownOpen, setIsPropertiesDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'HOME', href: '/' },
    {
      name: 'ABOUT',
      href: '/about',
      subItems: [
        { name: 'ABOUT US', href: '/about' },
        { name: 'OUR TEAM', href: '/team' },
        { name: 'HOW IT WORKS', href: '/how-it-works' }
      ]
    },
    {
      name: 'PROPERTIES',
      href: '/properties',
      subItems: [
        { name: 'ALL PROPERTIES', href: '/properties' },
        { name: 'GALLERY', href: '/gallery' }
      ]
    },
    { name: 'FAQ', href: '/faq' },
    { name: 'BLOG', href: '/blog' },
    { name: 'CONTACT', href: '/contact' }
  ];

  // Check current page
  const isHomePage = window.location.pathname === '/';
  const isDetailPage = window.location.pathname.includes('/blog/') || window.location.pathname.includes('/property/');
  
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled || isDetailPage
        ? 'bg-opacity-95 backdrop-blur-sm shadow-lg'
        : 'bg-transparent'
    }`} style={{
      ...(isScrolled || isDetailPage ? { backgroundColor: '#81be00' } : {})
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isScrolled || isDetailPage ? 'py-4' : 'py-6'
        }`}>
          {/* Request a Viewing Button */}
          <div className="hidden lg:block lg:mr-10 xl:mr-14">
            <a
              href="/request-viewing"
              className={`inline-block transition-all duration-300 px-5 py-2 uppercase tracking-wide text-sm md:text-base border border-white bg-white text-[#7b513a] hover:bg-white/90 hover:text-[#6a4532] ${
                isScrolled || isDetailPage
                  ? 'hover:text-[#7b513a]'
                  : 'hover:text-black'
              }`}
            >
              REQUEST A VIEWING
            </a>
          </div>

          {/* Main Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8 lg:ml-8 xl:ml-10">
            {menuItems.map((item) => (
              item.subItems ? (
                <DropdownMenu
                  key={item.name}
                  open={item.name === 'ABOUT' ? isAboutDropdownOpen : item.name === 'PROPERTIES' ? isPropertiesDropdownOpen : undefined}
                  onOpenChange={item.name === 'ABOUT' ? setIsAboutDropdownOpen : item.name === 'PROPERTIES' ? setIsPropertiesDropdownOpen : undefined}
                >
                  <DropdownMenuTrigger
                    className={`transition-colors duration-300 text-base md:text-lg font-medium tracking-wide flex items-center ${
                      isScrolled || isDetailPage
                        ? 'text-white hover:text-white/80'
                        : 'text-white hover:text-[#81be00]'
                    }`}
                    onMouseEnter={() => (item.name === 'ABOUT' ? setIsAboutDropdownOpen(true) : item.name === 'PROPERTIES' ? setIsPropertiesDropdownOpen(true) : null)}
                    onMouseLeave={() => (item.name === 'ABOUT' ? setIsAboutDropdownOpen(false) : item.name === 'PROPERTIES' ? setIsPropertiesDropdownOpen(false) : null)}
                  >
                    {item.name}
                    <ChevronDown size={16} className={`ml-1 transition-transform duration-300 ${(item.name === 'ABOUT' ? isAboutDropdownOpen : isPropertiesDropdownOpen) ? 'rotate-180' : ''}`} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="bg-white border border-gray-200 shadow-lg rounded-md p-2 animate-in fade-in-0 zoom-in-95"
                    onMouseEnter={() => (item.name === 'ABOUT' ? setIsAboutDropdownOpen(true) : setIsPropertiesDropdownOpen(true))}
                    onMouseLeave={() => (item.name === 'ABOUT' ? setIsAboutDropdownOpen(false) : setIsPropertiesDropdownOpen(false))}
                  >
                    {item.subItems.map((subItem) => (
                      <DropdownMenuItem key={subItem.name} asChild className="hover:bg-transparent">
                        <a href={subItem.href} className="cursor-pointer hover:text-[#81be00]">
                          {subItem.name}
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className={`transition-colors duration-300 text-base md:text-lg font-medium tracking-wide ${
                    isScrolled || isDetailPage
                      ? 'text-white hover:text-white/80'
                      : 'text-white hover:text-[#81be00]'
                  }`}
                >
                  {item.name}
                </a>
              )
            ))}
          </div>

          {/* Logo */}
          <div className="flex-1 lg:flex-none flex justify-center lg:justify-end lg:mx-8 xl:mx-12">
            <a href="/" className="flex items-center">
              <img
                src="https://ik.imagekit.io/hqhiltiie/WhatsApp%20Image%202025-10-10%20at%2019.44.26.jpeg?updatedAt=1760182338495"
                alt="Logo"
                className="h-18 md:h-24 w-auto object-contain"
              />
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`transition-colors duration-300 ${
                isScrolled || isDetailPage
                  ? 'text-white hover:text-white/80'
                  : 'text-white hover:text-[#81be00]'
              }`}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-[#7b513a] bg-opacity-95 backdrop-blur-sm p-4 mt-2 rounded-lg">
            <div className="flex flex-col space-y-4">
              <a
                href="/request-viewing"
                className="inline-block bg-transparent border border-white text-white hover:bg-white hover:text-[#7b513a] transition-all duration-300 px-6 py-2 uppercase tracking-wide text-sm text-center"
              >
                REQUEST A VIEWING
              </a>
              {menuItems.map((item) => (
                <div key={item.name}>
                  {item.subItems ? (
                    <>
                      <a
                        href={item.href}
                        className="text-white hover:text-[#81be00] transition-colors duration-300 text-base md:text-lg font-medium tracking-wide py-3 block"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </a>
                      {item.subItems.map((subItem) => (
                        <a
                          key={subItem.name}
                          href={subItem.href}
                          className="text-white hover:text-[#81be00] transition-colors duration-300 text-base md:text-lg font-medium tracking-wide py-3 block ml-4"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.name}
                        </a>
                      ))}
                    </>
                  ) : (
                    <a
                      href={item.href}
                      className="text-white hover:text-[#81be00] transition-colors duration-300 text-base md:text-lg font-medium tracking-wide py-3 block"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
